import { useUserStore } from '@/stores/user';
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { ElMessage } from 'element-plus';

// api文档地址 http://192.168.220.64:8080/swagger-ui/index.html#/
export interface ApiResponse<T = unknown> {
  code?: number; // 业务状态码
  message?: string;
  data: T;
}

const http: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
});

http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 为每次请求附加 token
    let token: string | null = null;
    try {
      const store = useUserStore();
      token = store.token;
    } catch {
      // store 未初始化时回退到 localStorage
      token = localStorage.getItem('token');
    }
    if (token) {
      // Axios v1+ may use AxiosHeaders instance; prefer set when available
      const maybeHeaders = config.headers as unknown;
      if (
        maybeHeaders &&
        typeof (maybeHeaders as { set?: (k: string, v: string) => void }).set === 'function'
      ) {
        (maybeHeaders as { set: (k: string, v: string) => void }).set(
          'Authorization',
          `Bearer ${token}`,
        );
      } else {
        const merged = {
          ...(config.headers as Record<string, string>),
          Authorization: `Bearer ${token}`,
        };
        config.headers = merged as unknown as InternalAxiosRequestConfig['headers'];
      }
    }
    return config;
  },
  (error) => Promise.reject(error instanceof Error ? error : new Error(String(error))),
);

// 统一抽取后端错误 message
export const extractErrorMessage = (err: unknown): string => {
  // 可能的后端错误格式：
  // 1) { code, message, data }
  // 2) { message }
  // 3) { error: '...' }
  // 4) 其它未知结构
  const defaultMsg = '请求失败';
  if (!err) return defaultMsg;
  // AxiosError 场景
  const axiosErr = err as AxiosError<unknown>;
  const respData = axiosErr.response?.data;
  if (respData) {
    if (typeof respData === 'string') return respData || defaultMsg;
    if (typeof respData === 'object') {
      const obj = respData as Record<string, unknown>;
      const m = (obj.message || obj.error) as string | undefined;
      if (m) return m;
    }
  }
  // 普通 Error
  if (err instanceof Error && err.message) return err.message;
  return defaultMsg;
};

http.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    return response; // 保持 AxiosResponse 类型，后续调用方通过 helper 提取 data
  },
  (error) => {
    // 在这里不做 UI 提示，只是规范化错误再抛出
    const wrapped = error instanceof Error ? error : new Error(String(error));
    // 增强：根据 HTTP 状态码或后端业务 code 做集中处理
    try {
      const axiosErr = error as AxiosError<ApiResponse<unknown>>;
      const status = axiosErr.response?.status;
      const bizCode = axiosErr.response?.data?.code;
      // 延迟导入避免首屏体积增长及循环（router 与 stores 内部互相引用）
      // 可扩展：将业务 code 与动作映射放在单独配置文件，例如：
      // const HANDLERS: Record<number, () => void> = { 40101: handleAuthExpired, 40301: handleForbidden };
      // 后续只需 HANDLERS[bizCode]?.();
      if (status === 401 || bizCode === 40101) {
        // token 失效：清理并跳转登录
        import('@/stores/user')
          .then(({ useUserStore }) => {
            const s = useUserStore();
            s.setToken('');
          })
          .catch(() => {});
        import('@/router')
          .then(({ default: routerModule }) => {
            const maybeRouter: { push?: (path: string) => void } =
              (routerModule as { router?: { push?: (p: string) => void } }).router || routerModule;
            maybeRouter.push?.('/login');
            return Promise.reject(wrapped);
          })
          .catch(() => {});
      } else if (status === 403 || bizCode === 40301) {
        // 权限不足提示（不自动跳转，保留当前页）

        import('@/stores/user')
          .then(({ useUserStore }) => {
            const s = useUserStore();
            s.setToken('');
          })
          .catch(() => {});
        import('@/router')
          .then(({ default: routerModule }) => {
            const maybeRouter: { push?: (path: string) => void } =
              (routerModule as { router?: { push?: (p: string) => void } }).router || routerModule;
            maybeRouter.push?.('/login');
            ElMessage({ type: 'error', message: '权限不足', duration: 2000 });
            return Promise.reject(wrapped);
          })
          .catch(() => {});
      }
    } catch {
      /* ignore secondary errors */
    }
    // 如果该请求未标记为静默，则在此统一弹出错误提示
    type SilentAxiosError = { config?: { silent?: boolean } };
    const errWithCfg = error as SilentAxiosError;
    const isSilent = Boolean(errWithCfg.config?.silent);
    if (!isSilent) {
      try {
        const msg = extractErrorMessage(error);
        ElMessage({ type: 'error', message: msg, duration: 2000 });
      } catch {
        ElMessage({ type: 'error', message: wrapped.message || '请求失败', duration: 2000 });
      }
    }
    return Promise.reject(wrapped);
  },
);

export const getData = <T = unknown>(resp: AxiosResponse<ApiResponse<T>>): T | ApiResponse<T> => {
  const r = resp.data;
  // 如果后端使用 {code, message, data} 格式可直接返回 r.data
  return r && 'data' in r ? r.data : (r as T);
};

export default http;
